import LRUCache from 'lru-cache'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'
import {
  PusherGrammarFeedback,
  PusherParaphraseFeedback,
  WtPusherEvent,
} from '@chegg-wt/core/state/paraphraser/types'
import { v4 as uuidv4 } from 'uuid'
import { FeedbackType } from '@chegg-wt/core/types/paraphraser'

const options = {
  // Max number of entries rough caclulation - 60 sentences*50 depth
  max: 300,

  // how long to live in ms - 24 hours
  ttl: 1000 * 60 * 60 * 24,

  // return stale items before removing from cache?
  allowStale: true,

  updateAgeOnGet: true,
  updateAgeOnHas: true,
}

class Cache {
  private cache: LRUCache<
    string,
    {
      sentenceId: string
      feedback: WtPusherEvent['feedback']
    }
  >

  private storedSentences: Map<
    string,
    { sentenceText: string; checks: Set<CheckTypeInput> }
  >

  constructor() {
    this.cache = new LRUCache(options)
    this.storedSentences = new Map()
  }

  private getSentenceAndInitiatedAtUnionId = (sentenceId: string, initatedAt: string) => {
    return `${sentenceId}_${initatedAt}}`
  }

  private getCacheKey = (checkType: CheckTypeInput, sentenceText: string): string => {
    return `${checkType}_${sentenceText}`
  }

  public storeTheSentenceText = (
    sentenceId: string,
    originalText: string,
    initiatedAt: string,
    checkType: CheckTypeInput
  ) => {
    const key = this.getSentenceAndInitiatedAtUnionId(sentenceId, initiatedAt)
    if (!this.storedSentences.has(key)) {
      this.storedSentences.set(key, {
        sentenceText: originalText,
        checks: new Set(),
      })
    }
    this.storedSentences.get(key).checks.add(checkType)
  }

  public removeTheSentenceText = (
    sentenceId: string,
    initatedAt: string,
    checkType: CheckTypeInput
  ) => {
    const key = this.getSentenceAndInitiatedAtUnionId(sentenceId, initatedAt)
    if (!this.storedSentences.has(key)) return
    const storedData = this.storedSentences.get(key)
    storedData.checks.delete(checkType)
    if (storedData.checks.size === 0) {
      // Remove this key to free up space if no check left
      this.storedSentences.delete(key)
    }
  }

  public addResponseToCacheUsingSentenceId = (
    sentenceId: string,
    initiatedAt: string,
    checkType: CheckTypeInput,
    feedback: WtPusherEvent['feedback']
  ) => {
    const storedSentenceKey = this.getSentenceAndInitiatedAtUnionId(
      sentenceId,
      initiatedAt
    )
    const { sentenceText } = this.storedSentences.get(storedSentenceKey) || {}
    if (sentenceText) {
      const cacheKey = this.getCacheKey(checkType, sentenceText)
      this.cache.set(cacheKey, {
        sentenceId,
        feedback,
      })
      this.removeTheSentenceText(sentenceText, initiatedAt, checkType)
    }
  }

  public addResponseToCacheUsingSentenceText = (
    sentenceText: string,
    sentenceId: string,
    checkType: CheckTypeInput,
    feedback: WtPusherEvent['feedback']
  ) => {
    const cacheKey = this.getCacheKey(checkType, sentenceText)
    this.cache.set(cacheKey, {
      sentenceId,
      feedback,
    })
  }

  private getParaphraserFeedback = (
    feedback: PusherParaphraseFeedback,
    updateId: boolean
  ) => {
    return {
      ...feedback,
      id: updateId ? uuidv4() : feedback.id,
    }
  }

  private getGrammarFeedback = (feedback: PusherGrammarFeedback, updateId: boolean) => {
    const feedbackCopy = { ...feedback }
    const sugestions = [...feedback.suggestions]
    if (updateId) {
      /* eslint-disable no-param-reassign */
      const suggestionsWithUpdatedId = sugestions.map((suggestion) => {
        return { ...suggestion, id: uuidv4() }
      })
      feedbackCopy.suggestions = suggestionsWithUpdatedId
    }
    return feedbackCopy
  }

  public getEventFromCache = (
    sentence: { id: string; originalText: string },
    initiatedAt: string,
    checkType: CheckTypeInput
  ): WtPusherEvent | null => {
    const cacheKey = this.getCacheKey(checkType, sentence.originalText)
    const cachedData = this.cache.get(cacheKey)
    if (!cachedData) return null

    const updateId = cachedData.sentenceId !== sentence.id

    const feedback =
      checkType === CheckTypeInput.PARAPHRASE
        ? this.getParaphraserFeedback(
            cachedData.feedback as PusherParaphraseFeedback,
            updateId
          )
        : this.getGrammarFeedback(cachedData.feedback as PusherGrammarFeedback, updateId)

    return {
      sentence: { id: sentence.id },
      initiatedAt,
      checkType,
      feedback,
    }
  }

  public addClarityReasonToCache = (sentenceText: string, reason: string) => {
    const cacheKey = this.getCacheKey(CheckTypeInput.PARAPHRASE, sentenceText)
    const cachedData = this.cache.get(cacheKey)
    if (cachedData?.feedback.feedbackType === FeedbackType.PARAPHRASE) {
      cachedData.feedback.reason = reason
    }
  }
}

const lruCache = new Cache()

export default lruCache
